import { useEffect } from "react";

import type { PlayerState } from "@/store/store";
import { getCurrentWordIndex } from "@/utils/alignment";

interface UsePlayerSyncProps {
  player: PlayerState;
  setPlayer: (
    update: PlayerState | ((prev: PlayerState) => PlayerState),
  ) => void;
  audioRef: React.RefObject<HTMLAudioElement>;
  seeking: boolean;
  currentWordIndex: number;
  setCurrentWordIndex: (index: number) => void;
  lastSyncedTimeRef: React.MutableRefObject<number>;
  isLoadingRef: React.MutableRefObject<boolean>;
}

export const usePlayerSync = ({
  player,
  setPlayer,
  audioRef,
  seeking,
  currentWordIndex,
  setCurrentWordIndex,
  lastSyncedTimeRef,
  isLoadingRef,
}: UsePlayerSyncProps) => {
  const track = player.track;

  /**
   * Effect: Sync audio src with track URL
   * Handles loading new tracks and resetting on track clear
   */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !track) return;

    const trackUrl = track.url || "";

    // Handle track cleared (stop)
    if (!trackUrl) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      lastSyncedTimeRef.current = 0;
      isLoadingRef.current = false;
      setCurrentWordIndex(-1);
      return;
    }

    // Handle new track
    if (audio.src !== trackUrl) {
      isLoadingRef.current = true;
      setPlayer((prev) => {
        const initialTime = prev.currentTime || 0;
        lastSyncedTimeRef.current = initialTime;

        return { ...prev, buffering: true };
      });

      // Clear and load new audio
      audio.pause();
      audio.src = trackUrl;
      audio.load();
      setCurrentWordIndex(-1);

      // Wait for track to be ready
      const handleCanPlay = () => {
        isLoadingRef.current = false;

        if (lastSyncedTimeRef.current > 0) {
          audio.currentTime = lastSyncedTimeRef.current;
        }

        setPlayer((prev) => ({
          ...prev,
          buffering: false,
        }));
      };

      const handleError = () => {
        console.error("[Player] Track load error");
        isLoadingRef.current = false;
        setPlayer((prev) => ({ ...prev, buffering: false, playing: false }));
      };

      audio.addEventListener("canplay", handleCanPlay, { once: true });
      audio.addEventListener("error", handleError, { once: true });

      return () => {
        audio.removeEventListener("canplay", handleCanPlay);
        audio.removeEventListener("error", handleError);
      };
    }
  }, [
    track,
    track?.id,
    setPlayer,
    setCurrentWordIndex,
    audioRef,
    lastSyncedTimeRef,
    isLoadingRef,
  ]);

  /**
   * Effect: Control playback state
   */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || isLoadingRef.current) return;

    if (player.playing) {
      audio.play().catch((error) => {
        console.error("[Player] Play error:", error);
        setPlayer((prev) => ({ ...prev, playing: false }));
      });
    } else {
      audio.pause();
    }
  }, [player.playing, player.buffering, setPlayer, audioRef, isLoadingRef]);

  /**
   * Effect: Sync playback settings (loop, rate)
   */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.loop = player.loop;
    audio.playbackRate = player.playbackRate;
  }, [player.loop, player.playbackRate, audioRef]);

  /**
   * Effect: Seek audio when currentTime changes externally
   */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || seeking || isLoadingRef.current) return;

    const targetTime = player.currentTime;
    const currentTime = lastSyncedTimeRef.current;
    const timeDiff = Math.abs(targetTime - currentTime);

    if (targetTime === 0 || timeDiff > 0.1) {
      audio.currentTime = targetTime;
      lastSyncedTimeRef.current = targetTime;
    }
  }, [player.currentTime, seeking, audioRef, lastSyncedTimeRef, isLoadingRef]);

  /**
   * Effect: Setup audio event listeners
   */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setPlayer((prev) => ({ ...prev, duration: audio.duration }));
    };

    const handleTimeUpdate = () => {
      if (seeking || isLoadingRef.current) return;

      const time = audio.currentTime;
      lastSyncedTimeRef.current = time;

      setPlayer((prev) => ({ ...prev, currentTime: time }));

      // Update word index
      if (track?.timings) {
        const wordIndex = getCurrentWordIndex(track.timings, time);
        if (wordIndex !== currentWordIndex) {
          setCurrentWordIndex(wordIndex);
        }
      }
    };

    const handlePlay = () => {
      if (!isLoadingRef.current) {
        setPlayer((prev) => ({ ...prev, playing: true }));
      }
    };

    const handlePause = () => {
      if (!isLoadingRef.current) {
        setPlayer((prev) => ({ ...prev, playing: false }));
      }
    };

    const handleEnded = () => {
      setPlayer((prev) => ({
        ...prev,
        playing: prev.loop,
        currentTime: prev.loop ? 0 : prev.duration,
      }));
    };

    const handleWaiting = () => {
      setPlayer((prev) => ({ ...prev, buffering: true }));
    };

    const handleCanPlay = () => {
      setPlayer((prev) => ({ ...prev, buffering: false }));
    };

    // Attach listeners
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("canplay", handleCanPlay);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("canplay", handleCanPlay);
    };
  }, [
    track?.timings,
    currentWordIndex,
    seeking,
    setPlayer,
    setCurrentWordIndex,
    audioRef,
    lastSyncedTimeRef,
    isLoadingRef,
  ]);

  /**
   * Effect: Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      setCurrentWordIndex(-1);
    };
  }, [setCurrentWordIndex]);
};
